// MSAL for the Quality SPA.
//
// ⚠ THIS APP HAS ITS OWN ENTRA REGISTRATION ("Majans Quality App"), and that is
// the difference from app-magic / app-commercial / app-purchasing, which share
// one. There is deliberately NO fallback client id here. A hardcoded fallback
// would mean a missing VITE_AZURE_CLIENT_ID sends the user to sign in against a
// sibling app's registration: sign-in appears to succeed, every token carries the
// wrong `aud`, and the API answers 401 `token_invalid` on every call with nothing
// on the screen pointing at the build. Failing at module load says it in one line.
import {
  LogLevel,
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
} from '@azure/msal-browser';
import { needsInteraction } from './authSignals';

export const MAJANS_TENANT_ID =
  import.meta.env.VITE_AZURE_TENANT_ID || 'd54794b1-f598-4c0f-a276-6039a39774ac';

/** Pairs with AUTH_BYPASS on the Functions host for local click-testing. Both
 *  sides must be on, and neither is ever set in a deployed environment. */
export const AUTH_BYPASS = import.meta.env.VITE_AUTH_BYPASS === 'true';

/**
 * Who the app pretends to be under AUTH_BYPASS.
 *
 * The default is a stand-in that holds no authority anywhere, so local clicking
 * exercises the REFUSED paths by default — which is the honest default, because
 * those are the paths this app is mostly made of.
 *
 * ⚠ Set `VITE_AUTH_BYPASS_UPN` to a real UPN only to click through a screen that
 * needs a grant, and set `AUTH_BYPASS_UPN` on the Functions host to the same
 * value. Both are VITE_/host env vars that are never set in a deployed build, and
 * the SERVER still takes the UPN from a validated token and ignores anything the
 * client says — so this value never reaches a record through a real deployment.
 * It DOES reach one through a local host running AUTH_BYPASS, which is why a
 * signature (approving a spec, recording a competency, verifying somebody else's
 * check) must never be made this way. Those rows are attestations about a person.
 */
const BYPASS_UPN = (import.meta.env.VITE_AUTH_BYPASS_UPN || 'dev.bypass@majans.com')
  .trim()
  .toLowerCase();

const CLIENT_ID = (import.meta.env.VITE_AZURE_CLIENT_ID || '').trim();

/**
 * Set when the build cannot sign anybody in. App.tsx renders it instead of
 * attempting a redirect.
 *
 * Reported rather than THROWN, and that distinction matters: a throw at module
 * scope happens while the import graph is still evaluating, before React exists,
 * so no error boundary and no try/catch in main.tsx can catch it. The user gets a
 * white page and the reason is only in the console — which on a docked iPad is
 * nowhere. A value the app can render puts the sentence on the screen.
 */
export const AUTH_CONFIG_ERROR: string | null =
  !CLIENT_ID && !AUTH_BYPASS
    ? 'VITE_AZURE_CLIENT_ID is not set. That is the "Majans Quality App" registration id — the ' +
      'app cannot sign anybody in without it, and must not borrow another app\'s.'
    : null;

// MSAL has to be constructible at import, because main.tsx and the offline queue
// both hold a reference to the instance. With no client id the placeholder keeps
// the constructor happy; nothing ever reaches the network with it, because
// AUTH_CONFIG_ERROR stops the app at the sign-in gate.
const MSAL_CLIENT_ID = CLIENT_ID || '00000000-0000-0000-0000-000000000000';

/** Built at runtime from the registration id, never pasted as a literal — the
 *  scope and the audience the API validates are then the same string by
 *  construction. */
export const API_SCOPE = `api://${CLIENT_ID}/access_as_user`;

export const msalConfig: Configuration = {
  auth: {
    clientId: MSAL_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${MAJANS_TENANT_ID}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    // ⚠ `navigateToLoginRequestUrl` and `storeAuthStateInCookie` are NOT set
    // here, and not by oversight: msal-browser v5 removed both from its
    // Configuration type, so copying a v4-era config in from another app is a
    // compile error rather than a silent no-op. Verified against the installed
    // v5 BrowserAuthOptions / CacheOptions.
  },
  cache: {
    // localStorage, not sessionStorage: the docked line iPad's browser is closed
    // and reopened between shifts, and a sign-in prompt at the start of every
    // shift is how an operator ends up sharing somebody else's session.
    cacheLocation: 'localStorage',
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error(message);
        else if (level === LogLevel.Warning) console.warn(message);
      },
    },
  },
};

export const loginRequest = { scopes: ['User.Read', API_SCOPE] };
export const apiScopes = { scopes: [API_SCOPE] };

export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * The signed-in Majans account, or null.
 *
 * Deliberately NOT `getAllAccounts()[0]`. An account from another tenant can
 * never be issued a token for `api://<clientId>`, so falling back to one turns a
 * fixable "you are on the wrong account" into a silent renewal that fails for
 * ever, with nothing on screen naming the account as the problem. Returning null
 * instead sends the user back through sign-in, which is recoverable.
 *
 * Same tenant filter as app-purchasing's `majansAccount` (app/src/config/auth.ts).
 */
export function majansAccount(): AccountInfo | null {
  const all = msalInstance.getAllAccounts();
  const active = msalInstance.getActiveAccount();
  if (active && active.tenantId === MAJANS_TENANT_ID) return active;
  return all.find((a) => a.tenantId === MAJANS_TENANT_ID) ?? null;
}

/** The UPN of the signed-in account, normalised the same way the API normalises
 *  the token claim — trimmed and lowercased. Attribution compares these strings,
 *  and the offline queue stamps this value into every item it enqueues, so a
 *  stray capital must not make two identities out of one person. */
export function currentUpn(): string {
  // AUTH_BYPASS has no MSAL account, so this would answer '' — and an app whose
  // every record is keyed on the UPN then behaves differently in the one mode
  // somebody uses to click through it. A named stand-in makes local testing
  // exercise the real paths (authority lookups, held-item ownership, the
  // landing redirect) instead of the not-signed-in ones.
  //
  // ⚠ Local only, and it cannot escape: AUTH_BYPASS is set from a VITE_ env var
  // that is never set in a deployed build, and the SERVER still takes the UPN
  // from a validated token and ignores anything the client says. This value
  // never reaches a record.
  if (AUTH_BYPASS) return BYPASS_UPN;
  return (majansAccount()?.username ?? '').trim().toLowerCase();
}

// One interactive redirect at a time, and at most one a minute. Without both
// guards a token that cannot be renewed — a revoked session, a removed grant —
// bounces the browser between the app and login.microsoftonline.com until
// somebody force-quits it.
let redirectInFlight = false;
const REDIRECT_AT_KEY = 'quality:tokenRedirectAt';

/**
 * An access token for the Quality API.
 *
 * Silent first. The usual silent failure is the hidden renewal iframe timing out
 * because the browser blocks third-party cookies, and the fix for that is a
 * top-level redirect, which is a first-party navigation and is not subject to it.
 *
 * Throws rather than returning a token-shaped placeholder: a request sent without
 * an Authorization header gets a 401 whose message is about the header, and the
 * real cause — this function — is nowhere in it.
 */
/** A token failure a person fixes by signing in: screens offer "Sign in again" for it. */
export class SignInError extends Error {
  readonly signIn = true;
}

/** Start an interactive sign-in from a screen's button (the same request the app signs in with). */
export function signInAgain(): void {
  void msalInstance.loginRedirect(loginRequest).catch((err: unknown) => console.error('[auth] sign-in redirect failed', err));
}

export async function acquireApiToken(): Promise<string> {
  const account = majansAccount();

  if (!account) {
    const others = msalInstance
      .getAllAccounts()
      .map((a) => a.username)
      .filter(Boolean);
    if (others.length > 0) {
      throw new Error(
        `Signed in as ${others.join(', ')}, which is not a Majans account, so no token for ` +
          'Quality can be issued. Sign out and sign back in with your @majans.com account.',
      );
    }
    throw new Error('Not signed in.');
  }

  // Make the resolved account sticky so a later call cannot lose a race to a
  // stray foreign account.
  if (msalInstance.getActiveAccount() !== account) msalInstance.setActiveAccount(account);

  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiScopes, account });
    return result.accessToken;
  } catch (err) {
    const lastRedirect = Number(sessionStorage.getItem(REDIRECT_AT_KEY) || 0);
    // Interaction-required AND timeouts — see needsInteraction for why a timeout
    // counts, and for the v5 code (`timed_out`) the old substring test missed.
    const interactive = needsInteraction(err);
    const code = (err as { errorCode?: string })?.errorCode;
    let redirectFailed = false;

    if (interactive && !redirectInFlight && Date.now() - lastRedirect > 60_000) {
      redirectInFlight = true;
      sessionStorage.setItem(REDIRECT_AT_KEY, String(Date.now()));
      console.warn('[auth] silent token acquisition failed; redirecting to re-authenticate', err);
      try {
        await msalInstance.acquireTokenRedirect({ ...apiScopes, account });
      } catch (redirectErr) {
        redirectInFlight = false;
        redirectFailed = true;
        console.error('[auth] interactive token redirect failed', redirectErr);
      }
    }

    // Carry the reason onto the screen, and make it say what is actually
    // happening. A stale session, a redirect already tried a moment ago and a
    // failure that is not about the session at all need different things from the
    // person reading it — and none of them is "the server refused this".
    // The code stays in the console (UI/UX pass, 3 Nov 2026: "(interaction_required)" on screen is plumbing).
    if (code) console.warn('[auth] token not renewed', code);
    if (interactive && redirectInFlight) {
      throw new SignInError('Your sign-in has expired. Taking you to Microsoft to sign in again…');
    }
    if (interactive && redirectFailed) {
      throw new SignInError('Your sign-in has expired, and the redirect to sign in again did not start. Sign in again; if it repeats, sign out and sign back in.');
    }
    if (interactive) {
      throw new SignInError('Your sign-in has expired. Sign in again to carry on.');
    }
    throw new SignInError('Your sign-in could not be renewed. Sign in again; if it repeats, sign out and sign back in.');
  }
}
